import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  Plus,
  ShoppingCart,
  Clock,
  LogOut,
  X,
  ChevronDown,
  ChevronRight,
  Settings,
  BookOpen,
  ExternalLink,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const Sidebar = ({ sidebarOpen, setSidebarOpen, handleLogout }) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);

  const menuItems = [
    { id: "dashboard", label: "Dashboard", icon: Home, path: "/dashboard" },
    {
      id: "deposits",
      label: "Deposits",
      icon: Plus,
      path: "/dashboard/deposits",
    },
    {
      id: "products",
      label: "Products",
      icon: ShoppingCart,
      hasDropdown: true,
      subItems: [
        {
          id: "affordable-data",
          label: "Buy Affordable Data",
          path: "/dashboard/products/affordable-data",
          walletType: "affordable_data",
        },
        {
          id: "airtime-data",
          label: "Buy Airtime & Data",
          path: "/dashboard/products/airtime-data",
          walletType: "airtime_data",
        },
      ],
    },
    {
      id: "history",
      label: "History",
      icon: Clock,
      path: "/dashboard/history",
    },
    {
      id: "api-management",
      label: "API Settings",
      icon: Settings,
      path: "/dashboard/api-management",
    },
    // 🆕 Added API Docs
    {
      id: "api-docs",
      label: "API Docs",
      icon: BookOpen,
      path: "https://docs.cinivestsys.com/",
      isExternal: true,
    },
  ];

  // Get user's name for display
  const firstName = user?.first_name || "User";
  const lastName = user?.last_name || "";

  const handleMenuClick = (item) => {
    if (item.hasDropdown) {
      setProductsDropdownOpen(!productsDropdownOpen);
    } else if (item.isExternal) {
      // 🆕 Handle external links
      window.open(item.path, "_blank", "noopener,noreferrer");
      setSidebarOpen(false);
    } else {
      navigate(item.path);
      setSidebarOpen(false);
    }
  };

  const handleSubMenuClick = (subItem) => {
    navigate(subItem.path);
    setSidebarOpen(false);
  };

  // Check if current route matches item
  const isActive = (item) => {
    if (item.isExternal) {
      return false; // External links are never "active"
    }
    if (item.hasDropdown) {
      return item.subItems.some((sub) => location.pathname === sub.path);
    }
    return location.pathname === item.path;
  };

  // Check if submenu item is active
  const isSubItemActive = (subItem) => {
    return location.pathname === subItem.path;
  };

  // Auto-open dropdown if we're on a products page
  React.useEffect(() => {
    const isOnProductsPage = location.pathname.startsWith(
      "/dashboard/products",
    );
    if (isOnProductsPage) {
      setProductsDropdownOpen(true);
    }
  }, [location.pathname]);

  return (
    <>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-yellow-900 backdrop-blur-[1px] bg-opacity-5 z-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Fixed Sidebar - Dark Theme */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-gray-900 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-6 border-b border-gray-700">
          <div className="flex items-center space-x-2">
            <img
              src="/logo.jpg"
              alt="Cinivest Systems"
              className="h-8 w-8 rounded-lg  "
            />
            <span className="font-bold text-white">Cinivest Pro</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 text-gray-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 mt-4">
          <div className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const itemIsActive = isActive(item);

              return (
                <div key={item.id}>
                  {/* Main Menu Item */}
                  <button
                    onClick={() => handleMenuClick(item)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-left transition-all ${
                      itemIsActive
                        ? "bg-yellow-400 text-black shadow-lg"
                        : "text-gray-300 hover:bg-gray-800 hover:text-white"
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </div>

                    <div className="flex items-center space-x-2">
                      {/* 🆕 External link indicator */}
                      {item.isExternal && (
                        <ExternalLink className="w-3 h-3 opacity-70" />
                      )}

                      {item.hasDropdown && (
                        <>
                          {itemIsActive && !item.hasDropdown && (
                            <div className="w-2 h-2 bg-black rounded-full"></div>
                          )}
                          {productsDropdownOpen ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </>
                      )}

                      {!item.hasDropdown &&
                        !item.isExternal &&
                        itemIsActive && (
                          <div className="w-2 h-2 bg-black rounded-full"></div>
                        )}
                    </div>
                  </button>

                  {/* Dropdown Submenu */}
                  {item.hasDropdown && productsDropdownOpen && (
                    <div className="mt-2 ml-4 space-y-1">
                      {item.subItems.map((subItem) => (
                        <button
                          key={subItem.id}
                          onClick={() => handleSubMenuClick(subItem)}
                          className={`w-full flex items-center space-x-3 px-4 py-2 text-left transition-all text-sm relative ${
                            isSubItemActive(subItem)
                              ? "text-yellow-400 bg-gray-800 border-l-4 border-yellow-400"
                              : "text-gray-400 hover:bg-gray-800 hover:text-white border-l-4 border-transparent"
                          }`}
                        >
                          <div className="w-2 h-2 rounded-full bg-current opacity-50"></div>
                          <span className="font-medium">{subItem.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>

        {/* User & Logout */}
        <div className="absolute bottom-0 w-full p-6 border-t border-gray-700">
          <div className="flex items-center space-x-2 mb-4 p-2 bg-gradient-to-r from-gray-800 to-gray-700 rounded-xl border border-gray-600 shadow-lg">
            <div className="relative">
              <div className="p-0.5 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full">
                <img
                  src="/avatar.png"
                  alt={`${firstName} ${lastName}`}
                  className="w-10 h-10 rounded-full object-cover bg-yellow-400"
                />
              </div>
              {/* Online status indicator */}
              <div className="absolute -bottom-0 right-1 w-2.5 h-2.5 bg-green-400 border-2 border-gray-800 rounded-full shadow-sm animate-pulse"></div>
            </div>
            <div className="flex-1">
              <p className="font-light text-white text-sm">
                <b className="font-bold">{firstName}</b> {lastName}
              </p>
              <p className="text-xs text-yellow-400 font-medium">Agent</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-3 px-4 py-3 text-red-400 hover:bg-red-900 hover:bg-opacity-20 rounded-xl transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
